@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginemodule

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.service.LizenzService
import org.example.project.service.ZustandManager
import org.example.project.utils.All

@Composable
fun ModulEditor() {
    val scope = rememberCoroutineScope()
    var modules by remember { mutableStateOf<List<All>>(emptyList()) }
    var creatingModule by remember { mutableStateOf(false) }
    var inlineEditModuleKey by remember { mutableStateOf<String?>(null) }
    var selectedModuleKey by remember { mutableStateOf<String?>(null) }
    var selectedModuleKeys by remember { mutableStateOf<Set<String>>(emptySet()) }
    var installedModuleIds by remember { mutableStateOf<Set<Int>>(emptySet()) }
    val inlineDrafts = remember { mutableStateMapOf<String, All>() }
    val moduleStates = remember { mutableStateMapOf<String, ModuleState>() }
    val progressMap = remember { mutableStateMapOf<String, Int>() }

    fun closeEditor() {
        inlineEditModuleKey = null
    }

    fun selectSingle(moduleKey: String?) {
        selectedModuleKey = moduleKey
        selectedModuleKeys = moduleKey?.let { setOf(it) } ?: emptySet()
    }

    fun findModuleByKey(moduleKey: String): All? =
        modules.firstOrNull { it.moduleVersionKey() == moduleKey }

    fun toggleSelection(moduleKey: String, multi: Boolean) {
        if (!multi) {
            selectSingle(moduleKey)
            return
        }
        val next = selectedModuleKeys.toMutableSet()
        if (!next.add(moduleKey)) next.remove(moduleKey)
        selectedModuleKeys = next
        selectedModuleKey = when {
            moduleKey in next -> moduleKey
            next.isNotEmpty() -> next.last()
            else -> null
        }
    }

    suspend fun reloadModules() {
        modules = ModuleApi.loadSystemModules()
            .map { it.copy(zustand = normalizeGlobalModuleState(it.zustand)) }
            .sortedWith(compareBy<All> { it.modulId }.thenBy { it.modulReferenz.trim() })

        installedModuleIds = ModuleApi.loadInstalledModuleIds()

        val availableKeys = modules.map { it.moduleVersionKey() }.toSet()
        selectedModuleKeys = selectedModuleKeys.filterTo(linkedSetOf()) { it in availableKeys }
        if (selectedModuleKeys.isEmpty()) selectedModuleKey = null
        else if (selectedModuleKey !in selectedModuleKeys) selectedModuleKey = selectedModuleKeys.lastOrNull()

        if (inlineEditModuleKey != null && modules.none { it.moduleVersionKey() == inlineEditModuleKey }) {
            inlineEditModuleKey = null
        }
    }

    LaunchedEffect(Unit) {
        reloadModules()
    }

    fun deriveState(module: ShopModuleMeta): ModuleState = deriveSysModuleState(module)

    suspend fun persistGlobalState(module: ShopModuleMeta, nextState: Int) {
        val existing = findModuleByKey(module.key)?.normalizeScreens() ?: return
        val updated = buildSysModuleStateChange(existing, nextState)
        ModuleApi.saveSystemModule(updated)
        LizenzService.syncAllKundenModuleStates()
        reloadModules()
    }

    fun openEditor(moduleKey: String) {
        selectSingle(moduleKey)
        inlineEditModuleKey = moduleKey
        val source = findModuleByKey(moduleKey)?.normalizeScreens()
        if (source != null) inlineDrafts[moduleKey] = source
    }

    val allModules = modules.map { it.toShopMeta() }
    val selectedModules = allModules.filter { it.key in selectedModuleKeys }
    val canDeleteSelected = selectedModules.isNotEmpty() && selectedModules.all { it.moduleId !in installedModuleIds }
    val editorSource = inlineEditModuleKey?.let { key -> findModuleByKey(key)?.normalizeScreens() }
    val editorDraft = inlineEditModuleKey?.let { key -> inlineDrafts[key] ?: editorSource }

    fun applySelectedState(nextState: Int) {
        if (selectedModules.isEmpty()) return
        scope.launch {
            selectedModules.forEach { module ->
                persistGlobalState(module, nextState)
            }
        }
    }

    fun deleteSelectedModule() {
        if (!canDeleteSelected) return
        val keysToRemove = selectedModules.map { it.key }.toSet()
        scope.launch {
            selectedModules.forEach { module ->
                ModuleApi.deleteSystemModule(module.moduleId)
            }
            LizenzService.syncAllKundenModuleStates()
            keysToRemove.forEach { inlineDrafts.remove(it) }
            closeEditor()
            selectedModuleKeys = emptySet()
            selectedModuleKey = null
            reloadModules()
        }
    }

    fun saveEditor() {
        val key = inlineEditModuleKey ?: return
        val draft = editorDraft ?: return
        scope.launch {
            if (draft.modulName.isBlank()) return@launch
            val existing = findModuleByKey(key)
            val prepared = prepareSysModuleForSave(draft, existing)
            if (shouldWarnOnSysModuleSave(
                    initialEnabledScreens = existing?.normalizeScreens()?.modulScreens?.filterValues { it }?.keys?.toSet() ?: emptySet(),
                    initialVersion = existing?.modulReferenz?.trim().orEmpty().ifBlank { "0.0.0" },
                    module = draft
                )
            ) return@launch
            val saved = ModuleApi.saveSystemModule(prepared) ?: prepared
            LizenzService.syncAllKundenModuleStates()
            reloadModules()
            inlineDrafts.remove(key)
            inlineDrafts[saved.moduleVersionKey()] = saved
            selectSingle(saved.moduleVersionKey())
            closeEditor()
        }
    }

    CreateLayout(
        modules = allModules,
        moduleStates = moduleStates,
        progressMap = progressMap,
        creatingModule = creatingModule,
        selectedModuleKey = selectedModuleKey,
        selectedModuleKeys = selectedModuleKeys,
        canDeleteSelected = canDeleteSelected,
        isEditing = inlineEditModuleKey != null,
        onSelectModuleKey = { selectSingle(it) },
        onToggleModuleSelection = { key, multi -> toggleSelection(key, multi) },
        onCreateModule = {
            if (creatingModule) return@CreateLayout
            creatingModule = true
            scope.launch {
                try {
                    val created = ModuleApi.createSystemModule()
                    reloadModules()
                    closeEditor()
                    created?.let { selectSingle(it.moduleVersionKey()) }
                } finally {
                    creatingModule = false
                }
            }
        },
        onEditSelected = {
            val key = selectedModuleKey ?: return@CreateLayout
            if (selectedModuleKeys.size == 1) openEditor(key)
        },
        onActivateSelected = { applySelectedState(ZustandManager.Modul.State.AKTIVIERT) },
        onSaveEditor = { saveEditor() },
        onDeleteSelected = { deleteSelectedModule() },
        onDeactivateSelected = { applySelectedState(ZustandManager.Modul.State.DEAKTIVIERT) },
        onInstall = {},
        onActivate = {},
        onDeactivate = {},
        onOpenDetails = { module -> openEditor(module.key) },
        editableModule = editorDraft,
        onEditableModuleChange = { updated ->
            val key = inlineEditModuleKey ?: return@CreateLayout
            inlineDrafts[key] = updated
        },
        onLoadModuleAssets = { ModuleApi.loadModuleAssetIds() },
        onUploadModuleAsset = { file, fileName -> ModuleApi.uploadModuleAsset(file, fileName) },
        onDeleteModuleAsset = { assetId -> ModuleApi.deleteModuleAsset(assetId) }
    )
}














